/**
 * Rest nodes — the optional in-band workbench. Issue #6.
 *
 * **Deliberately not a sanctum.** #1's phase 4 and this issue record the same
 * decision from two sides, and the distinction is the whole point:
 *
 *   sanctum    one per band boundary, guaranteed, on the spine, found by
 *              descending; heal, altar, cache, gate onward
 *   rest node  scattered inside a band, optional, out on the flanks, found by
 *              going sideways; reforge, stash access, crafting, vendors
 *
 * The main path is vertical. Rest nodes are why you would ever leave it. Fold
 * them into the sanctums and horizontal extent goes back to being a place the
 * biome palette happens in.
 *
 * The interaction is the one the project already teaches in its first thirty
 * seconds — walk to it, press E, no menu — copied from `weaponAltar.ts` and
 * `abilityCache.ts` rather than invented a third time (docs/11: reusing one
 * strong affordance beats adding a second weak one).
 *
 * MATERIALLY DISTINCT from a sanctum, and that is a design requirement rather
 * than dressing: a sanctum is `ADAMANT_SEAL`, the one thing nobody built and
 * nobody can remove; a rest node is ordinary biome shell, a room somebody made
 * out of what was to hand and that you could dig into from the side if you
 * wanted to. Reading which one you have found should not require walking in.
 *
 * DETERMINISM. Pure in `(seed, band, index)`. The one non-obvious input is
 * `snap`: the chamber floor is aligned to the same terrace lattice the ledge
 * pass and the corridor router use, so the doorway opens onto a gallery at its
 * own level instead of into the middle of a slab. `WorldGen.shelfFloorAt` is
 * itself pure in `(seed, x, y)`, so passing it through costs nothing.
 */

import { hash2 } from '../core/rng';

/** Interior width. A room, not a hall — the sanctum is the hall. */
export const REST_IN_W = 84;
/** Interior height. Comfortably over the 22-cell body, under a gallery period. */
export const REST_IN_H = 34;
/** Shell thickness, in biome shell material. Thin: this is not a gate. */
export const REST_SHELL = 4;
/**
 * How far off the spine the nearest rest node sits.
 *
 * 420 is not arbitrary: a sanctum reaches ±96 from its centre and drifts ±240,
 * so 336 is the furthest a sanctum can extend. Beyond 420 the two can never be
 * confused for one another, and finding one is unambiguously a decision to
 * leave the descent.
 */
export const REST_MIN_X = 420;
export const REST_SPAN_X = 520;
/** Two per band, one on each flank — see `restNodesInBand`. */
export const REST_PER_BAND = 2;
/** Bench reach. Slightly longer than the altar's: this is a room you work in. */
export const REST_RANGE = 26;

/** Height of the doorway in the spine-facing wall. Walked through, not dropped. */
export const REST_DOOR_H = 26;
/** How far the approach corridor reaches back toward the spine. */
export const REST_APPROACH = 96;

export interface RestNode {
  band: number;
  /** 0 or 1 within the band. Index 0 is always the western node. */
  index: number;
  x: number;
  /** World y of the top cell of the floor. On the terrace lattice. */
  floorY: number;
  /** World y of the lowest ceiling cell. */
  ceilY: number;
  x0: number;
  x1: number;
  /** The wall the doorway is cut into: the one facing the spine. */
  doorX: number;
  /** Which way the approach corridor runs, toward x = 0. */
  doorDir: -1 | 1;
  /** The workbench plinth's top. */
  bench: { x: number; y: number };
}

/**
 * The rest nodes inside a band.
 *
 * One per flank, always: a band whose optional content is all on one side is a
 * band where half the world is empty, and the player has no way to know which
 * half before walking it.
 *
 * `snap` aligns the floor to the terrace lattice; the identity function is a
 * reasonable answer for a caller that does not have one.
 */
export function restNodesInBand(
  seed: number,
  band: number,
  bandHeight: number,
  snap: (x: number, y: number) => number = (_x, y) => y,
): RestNode[] {
  const out: RestNode[] = [];
  for (let i = 0; i < REST_PER_BAND; i++) {
    const h = hash2(band, i, seed ^ 0x2e57d0);
    const dir: -1 | 1 = i === 0 ? -1 : 1;
    const x = dir * (REST_MIN_X + (h % REST_SPAN_X));

    // Kept clear of both band boundaries, because a sanctum lives at each of
    // them and the two must never be found as one place. A sanctum reaches 150
    // cells either side of its line; 220 is the nearest a chamber may start.
    const lo = band * bandHeight + 240;
    const span = bandHeight - 480;
    const want = lo + (((h >>> 9) % (span >> 1)) + (i * span) / 2);
    const floorY = snap(x, Math.round(want));

    const half = REST_IN_W >> 1;
    // The doorway faces the spine, so walking outward from the descent runs
    // you into the opening rather than into the back wall.
    const doorDir: -1 | 1 = x > 0 ? -1 : 1;
    out.push({
      band,
      index: i,
      x,
      floorY,
      ceilY: floorY - REST_IN_H - 1,
      x0: x - half,
      x1: x + half - 1,
      doorX: doorDir < 0 ? x - half : x + half - 1,
      doorDir,
      bench: { x: x + doorDir * -18, y: floorY - 3 },
    });
  }
  return out;
}

// ------------------------------------------------------------------ services

/**
 * What the bench can do for you, from the two `ash.rest.*` grants that are
 * still rest-node-specific — `reforge` is not a third: #158 found its
 * promised feature (re-slotting modules) already free everywhere via
 * `loadout.ts` (docs/04 §7), so `ash.rest.reforge` was repointed at unlocking
 * the Splice recipe instead, which surfaces through `unlockedRecipes()` and
 * the crafting panel exactly like any other recipe — nothing here needs to
 * name it.
 *
 * `stash` is deliberately not one of the ash-gated two. The `ash.stash.*`
 * unlocks buy stash *slots*; being at a rest node is what makes the stash
 * reachable at all, so gating access behind a fourth unlock would mean a
 * player who bought four slots still could not open them. That is the state
 * the game shipped in — `atRestNode` was never true, so the loadout header
 * read "stash locked" for the entire game (docs/14 §8.2).
 */
export interface RestServices {
  /** Stash access. True at any rest node. */
  stash: boolean;
  /** `ash.rest.mend` — restore HP. */
  mend: boolean;
  /** `ash.rest.altar` — swap a run-start traversal ability mid-run. */
  altar: boolean;
}

/** Map `Progression.unlockedRestNodes()` onto what the bench offers. */
export function restServices(unlocked: readonly string[]): RestServices {
  return {
    stash: true,
    mend: unlocked.includes('rest.mend'),
    altar: unlocked.includes('rest.altar'),
  };
}

/** True while the player is close enough to work at the bench. */
export function inRestRange(n: RestNode, px: number, py: number): boolean {
  const dx = n.bench.x - px;
  const dy = n.bench.y - py;
  return dx * dx + dy * dy < REST_RANGE * REST_RANGE;
}

/** The node the player is standing at, or null. For the integrator's per-frame poll. */
export function restNodeAt(
  nodes: readonly RestNode[],
  px: number,
  py: number,
): RestNode | null {
  for (const n of nodes) if (inRestRange(n, px, py)) return n;
  return null;
}
